import Game from '../../game';
import Scene from '../../scene/scene';
import Element from '../element';
import Ship from './ship';
import Equipment, { EquipmentParams } from './equipment';
import Projectile, { ProjectileParams } from '../projectile';
import Sound, { SoundParams } from '../../audio/sound';

import { getXByDistanceAndAngle, getYByDistanceAndAngle, getAngleBy2XY, getDiffBetween2Angles, getDistanceBy2XY } from '../../../utils/geometry';
import { Faction } from '../../../utils/faction';

import { projectiles as projectileSpriteConfig } from '../../../config/sprites.config';

export interface WeaponParams extends EquipmentParams {
  allowedRotationAngle?: number;
  timeBetweenShots?: number;
  timeBeforeNextShot?: number;
  range?: number;
  projectileParams?: ProjectileParams;
  soundParams?: SoundParams;
}

export default class Weapon extends Equipment implements WeaponParams {
  allowedRotationAngle = 0;
  timeBetweenShots = 100; // unit: ms
  timeBeforeNextShot = 0; // unit: ms
  range = 400; // unit: pixels
  projectileParams?: ProjectileParams;
  soundParams?: SoundParams;

  sound?: Sound
  prioritytarget?: Element;
  shootAngleThreshold = 5;
  isRangeDrawn = false;

  constructor(params: WeaponParams) {
    super(params);
    this.applyParams(params);

    if (this.soundParams)
      this.sound = new Sound(this.soundParams);
  }

  get angleToAllowShoot(): number {
    return this.allowedRotationAngle || this.shootAngleThreshold; // less or equal than 5° angle between the weapon and the target
  }

  getProjectileSpriteSrc(): string {
    const sprite = (this.ship.faction === Faction.PLAYER)
      ? projectileSpriteConfig.blueBeam
      : projectileSpriteConfig.redBeam
    return sprite.src;
  }

  equipTo(ship: Ship): Ship {
    super.equipTo(ship);
    this.ship.weapons.push(this);
    return ship;
  }

  updateCooldown(modifier: number): void {
    this.timeBeforeNextShot -= (modifier * 1000); // weapon cooldown
  }

  update(modifier: number): Weapon {
    super.update(modifier);
    if (this?.building?.built === false) {
      this.build(modifier);
      return this;
    }
    this.updateCooldown(modifier);

    if (this.ship.isManuallyControlled()) {
      this.updateByManualControl(modifier);
      return this;
    }

    /* Shoot (and aim towards) closest target if possible */
    const targetsInRange = this.getElementsInRange(
      this.getTargetsQuadtree({ includeHazards: this.ship.faction !== Faction.ENEMY }), // Enemies won't attack hazardous elements
      this.range
    );
    if (targetsInRange.length > 0) {
      const targetsByDistance = this.sortElementsByDistance(targetsInRange, 'asc');

      const targetsThatCanBeAimed = targetsByDistance
        .filter((element: Element) => {
          const coordsCouple = {
            x1: this.x,
            y1: this.y,
            x2: element.x,
            y2: element.y,
          };
          const angleBetweenWeaponAndTarget = getAngleBy2XY(coordsCouple);
          const distanceBetweenWeaponAndTarget = getDistanceBy2XY(coordsCouple);
          const diffAngle = getDiffBetween2Angles(
            this.getAbsoluteAngle(this.lookAngleRelativeDefault),
            angleBetweenWeaponAndTarget
          );
          return (
            (diffAngle <= this.angleToAllowShoot || this.projectileParams?.seeker)
            && distanceBetweenWeaponAndTarget - (element.width + element.height) / 2 <= this.range
          );
        });

      let targetToShoot: Element;
      if (this.prioritytarget && targetsThatCanBeAimed.find(e => e?.id === this.prioritytarget?.id))
        targetToShoot = this.prioritytarget;
      else
        targetToShoot = targetsThatCanBeAimed[0];

      if (targetToShoot) {
        const angleBetweenWeaponAndTarget = getAngleBy2XY({
          x1: this.x,
          y1: this.y,
          x2: targetToShoot.x,
          y2: targetToShoot.y,
        });
        if (this.allowedRotationAngle && !this.projectileParams?.seeker)
          this.rotateTowardsAngle(angleBetweenWeaponAndTarget, modifier);

        const diffAngle = getDiffBetween2Angles(
          this.lookAngle,
          angleBetweenWeaponAndTarget
        );

        const canShoot = (
          diffAngle < this.shootAngleThreshold
          || this.projectileParams?.seeker
        );
        if (canShoot)
          this.shoot(targetToShoot);
      }
      else
        this.rotateTowardsAngle(this.getAbsoluteAngle(this.lookAngleRelativeDefault), modifier);
    }

    return this;
  }

  updateByManualControl(modifier: number): void {
    const camera = this.ship.scene.camera;
    const manualControls = Game.instance.controlSchemes.manual;
    if (!this.ship.canStrafe)
      this.rotateTowardsAngle(getAngleBy2XY({
        x1: this.x,
        y1: this.y,
        x2: manualControls.coords.x + camera.x - camera.width / 2,
        y2: manualControls.coords.y + camera.y - camera.height / 2,
      }), modifier);
    else
      this.rotateTowardsAngle(this.getAbsoluteAngle(this.lookAngleRelativeDefault), modifier);

    if (manualControls.inputs.SHOOTING)
      this.shoot();
  }

  getProjectileAdditionalParams(): {
    scene: Scene,
    faction: number,
    context: CanvasRenderingContext2D,
    ship: Ship,
    range: number,
    x: number,
    y: number,
    moveAngle: number,
    lookAngle: number,
  } {
    const absoluteLookAngle = this.lookAngle;
    const projectileHeight = this?.projectileParams?.height || 20;
    const x = getXByDistanceAndAngle({
      x: this.x,
      angle: absoluteLookAngle,
      distance: this.height / 2 + projectileHeight / 2,
    });
    const y = getYByDistanceAndAngle({
      y: this.y,
      angle: absoluteLookAngle,
      distance: this.height / 2 + projectileHeight / 2,
    });

    const { ship, range } = this;
    const { scene, faction, context } = ship;

    return {
      scene,
      faction,
      context,
      ship,
      range,
      x,
      y,
      moveAngle: absoluteLookAngle,
      lookAngle: absoluteLookAngle,
    };
  }

  createProjectile(target?: Element): Projectile {
    this.timeBeforeNextShot = this.timeBetweenShots;
    /* Create new projectile */
    return new Projectile({
      ... this.projectileParams,
      ...this.getProjectileAdditionalParams(),
      /* Use the right sprite image for each faction's projectile if not defined */
      spriteParams: {
        ... this.projectileParams?.spriteParams,
        ...(!this.projectileParams?.spriteParams?.src) && { src: this.getProjectileSpriteSrc() }
      },
      ...(target && this.projectileParams?.seeker) && { target }, // seeker projectiles have a target set when fired
      weapon: this as Weapon,
    } as ProjectileParams).create() as Projectile;
  }

  shoot(target?: Element): void {
    if (this.timeBeforeNextShot > 0)
      return;

    this.createProjectile(target);

    /* Make sound */
    if (this.sound && this.ship.isInCamera)
      this.sound.play();
  }

  draw(): CanvasRenderingContext2D {
    if (this.building && !this.building.built) {
      this.drawConstructionBar();
    }

    const {
      ship: {
        context
      }
    } = this;
    context.save();
    this.drawRange();
    context.restore();
    super.draw();
    return context;
  }

  drawRange(): CanvasRenderingContext2D {
    const {
      ship: {
        context
      }
    } = this;
    if (!this.isRangeDrawn) return context;

    /* Draw weapon effective angle */
    const color = Game.instance.controlSchemes.rts.getElementHoverColor(this.ship);
    context.fillStyle = color;
    context.lineWidth = 2;
    context.globalAlpha = 0.05;
    context.beginPath();
    context.moveTo(this.x, this.y);

    const weaponAbsoluteAngle = this.getAbsoluteAngle(this.lookAngleRelativeDefault);
    const startAngle = Math.PI / 180 * -(weaponAbsoluteAngle + this.angleToAllowShoot);
    const endAngle = Math.PI / 180 * -(weaponAbsoluteAngle - this.angleToAllowShoot);

    context.arc(this.x, this.y, this.range, startAngle, endAngle);
    context.closePath();
    context.fill();
    context.globalAlpha = 1;
    return context;
  }
}
